import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { motion } from 'motion/react';
import {
  Palette,
  Check,
  FileText,
  GraduationCap,
  BookOpen,
  ArrowRight,
  Sparkles } from
'lucide-react';

// Define types directly here to avoid import issues
export interface ColorTheme {
  name: string;
  primary: string;
  secondary: string;
  accent: string;
  background?: string;
  gradient: string;
  preview: string[];
}

export interface StyleTemplate {
  id: string;
  name: string;
  description: string;
  features: string[];
  preview: string;
}

export interface Template {
  theme: ColorTheme;
  style: StyleTemplate;
  colorScheme: {
    backgroundColor: string;
    textColor: string;
    accentColor: string;
  };
}

export interface TemplateSelectorProps {
  presentationData: any;
  updatePresentationData: (updates: any) => void;
  onNext: () => void;
}

const COLOR_THEMES: ColorTheme[] = [
{
  name: 'Professional Blue',
  primary: '#2563eb',
  secondary: '#3b82f6',
  accent: '#60a5fa',
  gradient: 'from-blue-600 to-blue-700',
  preview: ['#2563eb', '#3b82f6', '#60a5fa', '#93c5fd']
},
{
  name: 'Modern Purple',
  primary: '#7c3aed',
  secondary: '#8b5cf6',
  accent: '#a78bfa',
  gradient: 'from-purple-600 to-purple-700',
  preview: ['#7c3aed', '#8b5cf6', '#a78bfa', '#c4b5fd']
},
{
  name: 'Corporate Green',
  primary: '#059669',
  secondary: '#10b981',
  accent: '#34d399',
  gradient: 'from-green-600 to-green-700',
  preview: ['#059669', '#10b981', '#34d399', '#6ee7b7']
},
{
  name: 'Warm Orange',
  primary: '#ea580c',
  secondary: '#f97316',
  accent: '#fb923c',
  gradient: 'from-orange-600 to-orange-700',
  preview: ['#ea580c', '#f97316', '#fb923c', '#fdba74']
},
{
  name: 'Tech Indigo',
  primary: '#4f46e5',
  secondary: '#6366f1',
  accent: '#818cf8',
  gradient: 'from-indigo-600 to-indigo-700',
  preview: ['#4f46e5', '#6366f1', '#818cf8', '#a5b4fc']
},
{
  name: 'Creative Pink',
  primary: '#db2777',
  secondary: '#ec4899',
  accent: '#f472b6',
  gradient: 'from-pink-600 to-pink-700',
  preview: ['#db2777', '#ec4899', '#f472b6', '#f9a8d4']
}];


const STYLE_TEMPLATES: StyleTemplate[] = [
{
  id: 'modern',
  name: 'Modern Minimal',
  description: 'Clean, minimal design with plenty of white space',
  features: ['Clean typography', 'Minimal layouts', 'Focus on content'],
  preview: 'modern-preview.jpg'
},
{
  id: 'professional',
  name: 'Professional',
  description: 'Corporate-style presentation with structured layouts',
  features: ['Structured layouts', 'Professional fonts', 'Data-focused'],
  preview: 'professional-preview.jpg'
},
{
  id: 'creative',
  name: 'Creative',
  description: 'Dynamic layouts with creative visual elements',
  features: ['Dynamic layouts', 'Creative elements', 'Visual impact'],
  preview: 'creative-preview.jpg'
},
{
  id: 'educational',
  name: 'Educational',
  description: 'Student-friendly design with engaging visuals',
  features: ['Student-friendly', 'Engaging visuals', 'Clear hierarchy'],
  preview: 'educational-preview.jpg'
}];


const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  presentationData,
  updatePresentationData,
  onNext
}) => {
  const [selectedTheme, setSelectedTheme] = useState<ColorTheme | null>(presentationData.template?.theme || null);
  const [selectedStyle, setSelectedStyle] = useState<StyleTemplate | null>(presentationData.template?.style || null);
  const [formData, setFormData] = useState({
    title: presentationData.title || '',
    subject: presentationData.subject || '',
    gradeLevel: presentationData.gradeLevel || '',
    description: presentationData.description || ''
  });

  const handleFormChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    updatePresentationData({ [field]: value });
  };

  const handleThemeSelect = (theme: ColorTheme) => {
    setSelectedTheme(theme);
    updatePresentationData({
      template: {
        ...presentationData.template,
        theme,
        colorScheme: {
          backgroundColor: theme.primary,
          textColor: '#ffffff',
          accentColor: theme.accent
        }
      }
    });
  };

  const handleStyleSelect = (style: StyleTemplate) => {
    setSelectedStyle(style);
    updatePresentationData({
      template: {
        ...presentationData.template,
        style
      }
    });
  };

  const isComplete = formData.title && formData.subject && formData.gradeLevel && selectedTheme && selectedStyle;

  const handleNext = () => {
    if (isComplete) {
      onNext();
    }
  };

  return (
    <div className="space-y-8" data-id="xq895fn6v" data-path="src/components/ai/TemplateSelector.tsx">
      {/* Basic Information */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6" data-id="mw8zetzbk" data-path="src/components/ai/TemplateSelector.tsx">

        <div className="flex items-center gap-3 mb-6" data-id="xt6lc7hy1" data-path="src/components/ai/TemplateSelector.tsx">
          <div className="p-2 bg-indigo-100 rounded-lg" data-id="q0yh5tnw7" data-path="src/components/ai/TemplateSelector.tsx">
            <FileText className="w-5 h-5 text-indigo-600" data-id="52kwkh8tf" data-path="src/components/ai/TemplateSelector.tsx" />
          </div>
          <h3 className="text-xl font-semibold" data-id="1s87ebac2" data-path="src/components/ai/TemplateSelector.tsx">Presentation Details</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="jjgcdvkyt" data-path="src/components/ai/TemplateSelector.tsx">
          <div className="space-y-2" data-id="mrmmhjkp5" data-path="src/components/ai/TemplateSelector.tsx">
            <Label htmlFor="title" data-id="eqe2ccvc8" data-path="src/components/ai/TemplateSelector.tsx">Presentation Title *</Label>
            <Input
              id="title"
              placeholder="Enter presentation title..."
              value={formData.title}
              onChange={(e) => handleFormChange('title', e.target.value)}
              className="focus:ring-2 focus:ring-indigo-500" data-id="nc5vdidik" data-path="src/components/ai/TemplateSelector.tsx" />

          </div>

          <div className="space-y-2" data-id="ydfku2oij" data-path="src/components/ai/TemplateSelector.tsx">
            <Label htmlFor="subject" data-id="8uwxuqzj8" data-path="src/components/ai/TemplateSelector.tsx">Subject *</Label>
            <Select value={formData.subject} onValueChange={(value) => handleFormChange('subject', value)} data-id="k0xlgnc86" data-path="src/components/ai/TemplateSelector.tsx">
              <SelectTrigger data-id="wtswqtwzs" data-path="src/components/ai/TemplateSelector.tsx">
                <SelectValue placeholder="Select subject..." data-id="11z0dpqol" data-path="src/components/ai/TemplateSelector.tsx" />
              </SelectTrigger>
              <SelectContent data-id="yfobaeqtg" data-path="src/components/ai/TemplateSelector.tsx">
                <SelectItem value="mathematics" data-id="1diyzo2mq" data-path="src/components/ai/TemplateSelector.tsx">Mathematics</SelectItem>
                <SelectItem value="science" data-id="1r34ejwjt" data-path="src/components/ai/TemplateSelector.tsx">Science</SelectItem>
                <SelectItem value="english" data-id="ljgt1lcfl" data-path="src/components/ai/TemplateSelector.tsx">English</SelectItem>
                <SelectItem value="history" data-id="o1cfqzskm" data-path="src/components/ai/TemplateSelector.tsx">History</SelectItem>
                <SelectItem value="geography" data-id="gajh7p3vb" data-path="src/components/ai/TemplateSelector.tsx">Geography</SelectItem>
                <SelectItem value="physics" data-id="rzcvphzxo" data-path="src/components/ai/TemplateSelector.tsx">Physics</SelectItem>
                <SelectItem value="chemistry" data-id="nd2z8z82c" data-path="src/components/ai/TemplateSelector.tsx">Chemistry</SelectItem>
                <SelectItem value="biology" data-id="bgzjbabkr" data-path="src/components/ai/TemplateSelector.tsx">Biology</SelectItem>
                <SelectItem value="computer-science" data-id="fe2oml4wh" data-path="src/components/ai/TemplateSelector.tsx">Computer Science</SelectItem>
                <SelectItem value="art" data-id="91z7zlxmi" data-path="src/components/ai/TemplateSelector.tsx">Art</SelectItem>
                <SelectItem value="other" data-id="di0e9y4da" data-path="src/components/ai/TemplateSelector.tsx">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2" data-id="royl2bce0" data-path="src/components/ai/TemplateSelector.tsx">
            <Label htmlFor="gradeLevel" data-id="z9phubc88" data-path="src/components/ai/TemplateSelector.tsx">Grade Level *</Label>
            <Select value={formData.gradeLevel} onValueChange={(value) => handleFormChange('gradeLevel', value)} data-id="u06vbv60o" data-path="src/components/ai/TemplateSelector.tsx">
              <SelectTrigger data-id="8w5l6y69a" data-path="src/components/ai/TemplateSelector.tsx">
                <SelectValue placeholder="Select grade level..." data-id="fftdk37xb" data-path="src/components/ai/TemplateSelector.tsx" />
              </SelectTrigger>
              <SelectContent data-id="6kti9gvrz" data-path="src/components/ai/TemplateSelector.tsx">
                <SelectItem value="elementary" data-id="1l8tqeepg" data-path="src/components/ai/TemplateSelector.tsx">Elementary (K-5)</SelectItem>
                <SelectItem value="middle" data-id="scc41eqsi" data-path="src/components/ai/TemplateSelector.tsx">Middle School (6-8)</SelectItem>
                <SelectItem value="high" data-id="6nlbejvgb" data-path="src/components/ai/TemplateSelector.tsx">High School (9-12)</SelectItem>
                <SelectItem value="college" data-id="c21ulsl3s" data-path="src/components/ai/TemplateSelector.tsx">College</SelectItem>
                <SelectItem value="adult" data-id="lqwqkqn86" data-path="src/components/ai/TemplateSelector.tsx">Adult Education</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2 md:col-span-1" data-id="p876frtgb" data-path="src/components/ai/TemplateSelector.tsx">
            <Label htmlFor="description" data-id="9oyf5x2f9" data-path="src/components/ai/TemplateSelector.tsx">Description (Optional)</Label>
            <Textarea
              id="description"
              placeholder="Brief description of the presentation content..."
              value={formData.description}
              onChange={(e) => handleFormChange('description', e.target.value)}
              className="h-20 resize-none" data-id="8gwefugkz" data-path="src/components/ai/TemplateSelector.tsx" />

          </div>
        </div>
      </motion.div>

      {/* Color Theme Selection */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="space-y-6" data-id="1h3zitw1y" data-path="src/components/ai/TemplateSelector.tsx">

        <div className="flex items-center gap-3 mb-6" data-id="jh9ae9il9" data-path="src/components/ai/TemplateSelector.tsx">
          <div className="p-2 bg-purple-100 rounded-lg" data-id="0virzbqn3" data-path="src/components/ai/TemplateSelector.tsx">
            <Palette className="w-5 h-5 text-purple-600" data-id="n01x0xgkj" data-path="src/components/ai/TemplateSelector.tsx" />
          </div>
          <h3 className="text-xl font-semibold" data-id="h2ojo4sz8" data-path="src/components/ai/TemplateSelector.tsx">Color Theme</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="lgnc1xjx0" data-path="src/components/ai/TemplateSelector.tsx">
          {COLOR_THEMES.map((theme) =>
          <Card
            key={theme.name}
            className={`cursor-pointer transition-all duration-300 hover:shadow-lg ${
            selectedTheme?.name === theme.name ?
            'ring-2 ring-indigo-500 shadow-lg' :
            'hover:ring-2 hover:ring-gray-300'}`
            }
            onClick={() => handleThemeSelect(theme)} data-id="3lca2055z" data-path="src/components/ai/TemplateSelector.tsx">

              <CardContent className="p-4" data-id="4d9m7e1o1" data-path="src/components/ai/TemplateSelector.tsx">
                <div className="flex items-center justify-between mb-3" data-id="pwob2ejoa" data-path="src/components/ai/TemplateSelector.tsx">
                  <h4 className="font-medium" data-id="rjjmg59ko" data-path="src/components/ai/TemplateSelector.tsx">{theme.name}</h4>
                  {selectedTheme?.name === theme.name &&
                <div className="p-1 bg-indigo-100 rounded-full" data-id="y8g5etr90" data-path="src/components/ai/TemplateSelector.tsx">
                      <Check className="w-4 h-4 text-indigo-600" data-id="tby633hhm" data-path="src/components/ai/TemplateSelector.tsx" />
                    </div>
                }
                </div>
                
                <div className="flex gap-2 mb-3" data-id="whmgtr9y0" data-path="src/components/ai/TemplateSelector.tsx">
                  {theme.preview.map((color, index) =>
                <div
                  key={index}
                  className="w-8 h-8 rounded-lg shadow-sm"
                  style={{ backgroundColor: color }} data-id="oi9zkiyz7" data-path="src/components/ai/TemplateSelector.tsx" />

                )}
                </div>
                
                <div className={`w-full h-2 rounded-full bg-gradient-to-r ${theme.gradient}`} data-id="6c07k6zap" data-path="src/components/ai/TemplateSelector.tsx" />
              </CardContent>
            </Card>
          )}
        </div>
      </motion.div>

      {/* Style Template Selection */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="space-y-6" data-id="0lbc28ukk" data-path="src/components/ai/TemplateSelector.tsx">

        <div className="flex items-center gap-3 mb-6" data-id="wp8e59sey" data-path="src/components/ai/TemplateSelector.tsx">
          <div className="p-2 bg-green-100 rounded-lg" data-id="28xzp20g9" data-path="src/components/ai/TemplateSelector.tsx">
            <BookOpen className="w-5 h-5 text-green-600" data-id="jnr10vkzy" data-path="src/components/ai/TemplateSelector.tsx" />
          </div>
          <h3 className="text-xl font-semibold" data-id="8uyirzqg1" data-path="src/components/ai/TemplateSelector.tsx">Design Style</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="dvc5nw214" data-path="src/components/ai/TemplateSelector.tsx">
          {STYLE_TEMPLATES.map((style) =>
          <Card
            key={style.id}
            className={`cursor-pointer transition-all duration-300 hover:shadow-lg ${
            selectedStyle?.id === style.id ?
            'ring-2 ring-indigo-500 shadow-lg' :
            'hover:ring-2 hover:ring-gray-300'}`
            }
            onClick={() => handleStyleSelect(style)} data-id="e32qga65j" data-path="src/components/ai/TemplateSelector.tsx">

              <CardContent className="p-6" data-id="edi6brydd" data-path="src/components/ai/TemplateSelector.tsx">
                <div className="flex items-start justify-between mb-4" data-id="mdez0si1k" data-path="src/components/ai/TemplateSelector.tsx">
                  <div data-id="mrc0igowa" data-path="src/components/ai/TemplateSelector.tsx">
                    <h4 className="text-lg font-semibold mb-2" data-id="nvvc7qx2w" data-path="src/components/ai/TemplateSelector.tsx">{style.name}</h4>
                    <p className="text-gray-600 text-sm" data-id="40w57kcl6" data-path="src/components/ai/TemplateSelector.tsx">{style.description}</p>
                  </div>
                  {selectedStyle?.id === style.id &&
                <div className="p-1 bg-indigo-100 rounded-full" data-id="svr9xqkm0" data-path="src/components/ai/TemplateSelector.tsx">
                      <Check className="w-5 h-5 text-indigo-600" data-id="zbp6b8xhc" data-path="src/components/ai/TemplateSelector.tsx" />
                    </div>
                }
                </div>

                <div className="space-y-2" data-id="pm56torcj" data-path="src/components/ai/TemplateSelector.tsx">
                  {style.features.map((feature, index) =>
                <div key={index} className="flex items-center gap-2 text-sm text-gray-600" data-id="p37eve0sb" data-path="src/components/ai/TemplateSelector.tsx">
                      <div className="w-1.5 h-1.5 bg-gray-400 rounded-full" data-id="w3f9w7bij" data-path="src/components/ai/TemplateSelector.tsx" />
                      {feature}
                    </div>
                )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </motion.div>

      {/* Selection Summary */}
      {(selectedTheme || selectedStyle || formData.title) &&
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-gradient-to-br from-indigo-50 to-purple-50 p-6 rounded-lg border border-indigo-200" data-id="xbyr645ay" data-path="src/components/ai/TemplateSelector.tsx">

          <h4 className="text-lg font-semibold mb-4 flex items-center gap-2" data-id="uwdy68030" data-path="src/components/ai/TemplateSelector.tsx">
            <Sparkles className="w-5 h-5 text-indigo-600" data-id="8kz3na5qz" data-path="src/components/ai/TemplateSelector.tsx" />
            Selection Summary
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="jhmtvshpc" data-path="src/components/ai/TemplateSelector.tsx">
            {formData.title &&
          <div className="space-y-1" data-id="yrhgzcuot" data-path="src/components/ai/TemplateSelector.tsx">
                <p className="text-sm text-gray-600" data-id="gbxz7zasn" data-path="src/components/ai/TemplateSelector.tsx">Title</p>
                <Badge variant="outline" className="text-xs" data-id="g9yqicdel" data-path="src/components/ai/TemplateSelector.tsx">
                  {formData.title}
                </Badge>
              </div>
          }
            
            {formData.subject &&
          <div className="space-y-1" data-id="4b34bbbz4" data-path="src/components/ai/TemplateSelector.tsx">
                <p className="text-sm text-gray-600" data-id="hp4hpsknp" data-path="src/components/ai/TemplateSelector.tsx">Subject</p>
                <Badge variant="outline" className="text-xs" data-id="3ldeeifwh" data-path="src/components/ai/TemplateSelector.tsx">
                  {formData.subject}
                </Badge>
              </div>
          }
            
            {selectedTheme &&
          <div className="space-y-1" data-id="akhh7bsyd" data-path="src/components/ai/TemplateSelector.tsx">
                <p className="text-sm text-gray-600" data-id="oimfr42j3" data-path="src/components/ai/TemplateSelector.tsx">Color Theme</p>
                <Badge variant="outline" className="text-xs" data-id="stbwj0i6p" data-path="src/components/ai/TemplateSelector.tsx">
                  {selectedTheme.name}
                </Badge>
              </div>
          }
            
            {selectedStyle &&
          <div className="space-y-1" data-id="d1r8ped4d" data-path="src/components/ai/TemplateSelector.tsx">
                <p className="text-sm text-gray-600" data-id="elxar3yk6" data-path="src/components/ai/TemplateSelector.tsx">Design Style</p>
                <Badge variant="outline" className="text-xs" data-id="vidd43ml7" data-path="src/components/ai/TemplateSelector.tsx">
                  {selectedStyle.name}
                </Badge>
              </div>
          }
          </div>
        </motion.div>
      }

      {/* Next Button */}
      <div className="flex justify-end pt-6" data-id="4htv2i6r5" data-path="src/components/ai/TemplateSelector.tsx">
        <Button
          onClick={handleNext}
          disabled={!isComplete}
          className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
          size="lg" data-id="pz69fc5vo" data-path="src/components/ai/TemplateSelector.tsx">

          Continue to Content Planning
          <ArrowRight className="w-4 h-4 ml-2" data-id="dx6fxwsta" data-path="src/components/ai/TemplateSelector.tsx" />
        </Button>
      </div>
    </div>);

};

export default TemplateSelector;